import {
  ButtonNotFoundElementDataType,
  NotFoundElementDataType,
} from '@/components/sections/builder/elements/body/not-found/type';
import InputLink from '@/components/sections/builder/settings/input-link';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';
import { ElementProps } from '@/components/sections/builder/types';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { cn, isEmpty } from '@/lib/utils';
import { ThemeButtonType, useBuilderStore } from '@/stores/builder';
import { SquareMousePointerIcon } from 'lucide-react';
import Link from 'next/link';
import { shallow } from 'zustand/shallow';

const defaultData: ButtonNotFoundElementDataType = {
  name: 'Button',
  button1Label: 'Continue Shopping',
  button1Link: '/',
  button1Outline: false,
  button2Label: '',
  button2Link: '',
  button2Outline: false,
  gap: 2,
  spacing: 0,
  padding: {
    top: 10,
    right: 10,
    bottom: 10,
    left: 10,
  },
};

export const ElementContent = ({ element, preview }: ElementProps) => {
  const data = (element.data || defaultData) as ButtonNotFoundElementDataType;
  const parentData = (element.parentData || {}) as NotFoundElementDataType;

  const themeButton = element.theme?.button || ({} as ThemeButtonType);

  const isExternal1 = data.button1Link?.includes('http');
  const isExternal2 = data.button2Link?.includes('http');

  return (
    <div
      className={cn(
        'flex w-full items-center',
        parentData.alignment?.includes('Left') && 'justify-start',
        (parentData.alignment?.includes('Center') || !parentData.alignment) && 'justify-center',
        parentData.alignment?.includes('Right') && 'justify-end'
      )}
      style={
        {
          '--btn-radius': `${themeButton.borderCornerRadius}px`,
          gap: `${data.gap}px`,
          marginTop: `${data.spacing}px`,
        } as React.CSSProperties
      }
    >
      {!!data.button1Label && (
        <Link
          href={
            preview
              ? '#'
              : isExternal1
              ? data.button1Link || '#'
              : `/${(data.button1Link || '').replace(/^\/+/, '')}`
          }
          className={cn(
            'rounded-[--btn-radius] text-sm transition-all duration-100 hover:opacity-80',
            isEmpty(parentData.colorScheme)
              ? 'theme_button'
              : data.button1Outline
              ? 'border border-[--btn-outline] bg-transparent text-[--btn-outline]'
              : 'bg-[--btn-bg] text-[--btn-label]'
          )}
          style={{
            padding: `${data.padding?.top || defaultData?.padding?.top}px ${
              data.padding?.right || defaultData?.padding?.right
            }px ${data.padding?.bottom || defaultData?.padding?.bottom}px ${
              data.padding?.left || defaultData?.padding?.left
            }px`,
          }}
          target={isExternal1 ? '_blank' : undefined}
          rel={isExternal1 ? 'noopener noreferrer' : undefined}
          data-animation='true'
        >
          {data.button1Label}
        </Link>
      )}
      {!!data.button2Label && (
        <Link
          href={
            preview
              ? '#'
              : isExternal2
              ? data.button2Link || '#'
              : `/${(data.button2Link || '').replace(/^\/+/, '')}`
          }
          className={cn(
            'rounded-[--btn-radius] text-sm transition-all duration-100 hover:opacity-80',
            isEmpty(parentData.colorScheme)
              ? 'theme_button'
              : data.button2Outline
              ? 'border border-[--btn-outline] bg-transparent text-[--btn-outline]'
              : 'bg-[--btn-bg] text-[--btn-label]'
          )}
          style={{
            padding: `${data.padding?.top || defaultData?.padding?.top}px ${
              data.padding?.right || defaultData?.padding?.right
            }px ${data.padding?.bottom || defaultData?.padding?.bottom}px ${
              data.padding?.left || defaultData?.padding?.left
            }px`,
          }}
          target={isExternal2 ? '_blank' : undefined}
          rel={isExternal2 ? 'noopener noreferrer' : undefined}
          data-animation='true'
        >
          {data.button2Label}
        </Link>
      )}
    </div>
  );
};

export const ElementSetting = ({ element }: ElementProps) => {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ButtonNotFoundElementDataType;

  return (
    <div>
      <h1 className='mb-4 text-sm font-bold'>Button</h1>
      <div className='my-4'>
        <label className='mb-2 block'>First button label</label>
        <Input
          onChange={(e) =>
            builder.setChildData(element.parentId as string, element.id, {
              button1Label: e.target.value,
            })
          }
          name='button1Label'
          type='text'
          size={100}
          defaultValue={data.button1Label}
          className='w-full'
          placeholder='Label'
        />
        <span className='text-gray-500'>Leave the label blank to hide the button.</span>
      </div>
      <div className='my-4'>
        <label className='mb-2 block'>First button link</label>
        <InputLink
          name='button1Link'
          value={data.button1Link || ''}
          onChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, {
              button1Link: val,
            })
          }
          placeholder='Search or enter a link'
        />
      </div>
      <div className='mb-4 flex items-center gap-2'>
        <label>Use outline button</label>
        <Switch
          className='ml-auto'
          checked={data.button1Outline || false}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              button1Outline: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='my-4'>
        <label className='mb-2 block'>Second button label</label>
        <Input
          onChange={(e) =>
            builder.setChildData(element.parentId as string, element.id, {
              button2Label: e.target.value,
            })
          }
          name='button2Label'
          type='text'
          size={100}
          defaultValue={data.button2Label}
          className='w-full'
          placeholder='Label'
        />
        <span className='text-gray-500'>Leave the label blank to hide the button.</span>
      </div>
      <div className='my-4'>
        <label className='mb-2 block'>Second button link</label>
        <InputLink
          name='button2Link'
          value={data.button2Link || ''}
          onChange={(val) =>
            builder.setChildData(element.parentId as string, element.id, {
              button2Link: val,
            })
          }
          placeholder='Search or enter a link'
        />
      </div>
      <div className='mb-4 flex items-center gap-2'>
        <label>Use outline button</label>
        <Switch
          className='ml-auto'
          checked={data.button2Outline || false}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              button2Outline: !!checked,
            });
          }}
          aria-readonly
        />
      </div>

      <Separator className='my-4' />

      <h1 className='mb-3 text-sm font-bold'>Layout</h1>
      <InputSlider
        label='Button Gap'
        value={data.gap || Number(defaultData.gap)}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            gap: val,
          })
        }
      />
      <InputSlider
        label='Button Spacing'
        value={data.spacing || Number(defaultData.spacing)}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            spacing: val,
          })
        }
      />
      <PaddingSetting
        value={data.padding}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, { padding: v })
        }
      />
    </div>
  );
};

const ButtonNotFound = {
  type: 'not_found_button',
  group: 'not_found',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: SquareMousePointerIcon,
  Icon: SquareMousePointerIcon,
  childs: [],
};

export default ButtonNotFound;
